import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Building2, ArrowRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const fillDemo = () => {
    setEmail("demo@cashflowpro.com");
    setPassword("Demo@2026");
    setErr("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      nav(loc.state?.from || "/app", { replace: true });
    } else {
      setErr(res.error);
    }
  };

  return (
    <div className="min-h-screen flex bg-paper" data-testid="login-page">
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-2.5 mb-12" data-testid="login-logo">
            <div className="w-8 h-8 rounded-md bg-forest flex items-center justify-center text-white">
              <Building2 className="w-4 h-4" strokeWidth={1.75} />
            </div>
            <span className="font-heading text-xl font-semibold">BizCashflow <span className="text-forest">Pros</span></span>
          </Link>

          <h1 className="font-heading text-4xl font-medium tracking-tight mb-2">Welcome back.</h1>
          <p className="text-ink-light mb-10">Sign in to your business workspace.</p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="text-sm font-medium text-ink mb-1.5 block">Email</label>
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                data-testid="login-email-input"
                className="w-full bg-white border border-border rounded-lg px-3.5 py-2.5 text-base focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-ink mb-1.5 block">Password</label>
              <input
                type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                data-testid="login-password-input"
                className="w-full bg-white border border-border rounded-lg px-3.5 py-2.5 text-base focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none"
              />
            </div>
            {err && <div className="text-sm text-destructive" data-testid="login-error">{err}</div>}
            <button
              type="submit" disabled={loading}
              data-testid="login-submit-button"
              className="w-full btn-forest inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? "Signing in…" : <>Sign in <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          <div className="mt-6 text-sm text-ink-light space-y-1">
            <div>Accountant? <Link to="/register" className="text-forest font-medium hover:underline" data-testid="link-to-register">Create a firm account</Link></div>
            <div>Client? <Link to="/onboard" className="text-forest font-medium hover:underline" data-testid="link-to-onboard">Sign up for the portal</Link></div>
          </div>

          <div className="mt-10 p-4 bg-forest/5 rounded-lg text-xs text-ink-light" data-testid="demo-hint">
            <div className="font-medium text-forest mb-1">Try the demo</div>
            <div className="num">demo@cashflowpro.com / Demo@2026</div>
            <button
              type="button"
              onClick={fillDemo}
              data-testid="use-demo-button"
              className="mt-2 text-forest font-medium hover:underline"
            >
              Use demo credentials →
            </button>
            <div className="mt-1">Loaded with sample transactions, debts and tax data.</div>
          </div>
        </div>
      </div>

      <div className="hidden lg:block lg:w-1/2 bg-forest relative overflow-hidden">
        <div className="absolute inset-0 grain"></div>
        <div className="relative h-full flex flex-col justify-end p-14 text-white">
          <div className="overline text-forest-50 mb-6">Why owners switch</div>
          <blockquote className="font-heading text-3xl font-medium leading-snug max-w-md">
            "BizCashflow Pros paid for itself in the first Tuesday. I'd seen $4,200 in deductions I was leaving on the table."
          </blockquote>
          <div className="mt-6 text-sm text-forest-50">— Priya Shah, Studio Twelve</div>
        </div>
      </div>
    </div>
  );
}
